<template>
  <div class="tms-dashboard">
    <div class="content-body">
      <div class="nav-bar-container">
        <div class="nav-bar clearfix">
          <div class="welcome">
            <span class="summary-hello">{{ $t("hello") }}</span>
            <span class="summary-name">{{ (currentUserContact || {}).name || currentUser.name }}</span>
          </div>
          <div class="summary-items" />
        </div>
      </div>
      <div class="tenant-dashboard__wrapper">
        <DynamicTabsContainer
          :key="componentKey"
          :tabTemplates="shouldRenderTabTemplates"
          @tabClick="replaceRouteWithTabIndex"
        >
          <template
            v-if="hotbTabPinIndex !== -1 && showHotbTabPin"
            #[`icon-${hotbTabPinIndex}`]
          >
            <HKLPinBadge :inline="true" />
          </template>
          <template
            v-if="fitoutTabPinIndex !== -1 && showFitoutTabPin"
            #[`icon-${fitoutTabPinIndex}`]
          >
            <HKLPinBadge :inline="true" />
          </template>
          <template
            v-if="directoryTabPinIndex !== -1 && showDirectoryTabPin"
            #[`icon-${directoryTabPinIndex}`]
          >
            <HKLPinBadge :inline="true" />
          </template>
        </DynamicTabsContainer>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { computed, ref, unref } from 'vue-demi'
import DynamicTabsContainer from '@/controls/DynamicTabsContainer'
import HKLPinBadge from '@/controls/Badge/HKLPinBadge.vue'
import SwitchRegionsEventMixin from '@/modules/SwitchRegionsEventMixin'
import Directories from '@/modules/dashboards/Directories'
import HandoverTakeback from '@/modules/dashboards/HandoverTakeback'
import MyTenantProjects from '@/modules/dashboards/MyTenantProjects'
import {
  useDirectoryTabPin,
  useFitoutTabPin,
  useHotbTabPin,
} from '@/modules/dashboards/engineerDashboardTabPinHooks'
import { useStoreGetters } from '@/store/index'
import { aclHelper, ccdPermsTypes, tmsPermsTypes } from '@/utils/aclHelper'
import EventBus from '@/utils/eventBus'
import i18n from '@/utils/i18n'
import { useCanReadDirectoryDashboard } from '@/modules/dashboards/hook'
import EngineerGreenOperationVue from './EngineerGreenOperation.vue'

const I18N_KEY_DIRECTORY = 'directory.label.directory'
const I18N_KEY_HANDOVER_TAKEBACK = 'handoverTakeback'
const I18N_KEY_FITOUT_REINSTATEMENT = 'tender.general.fitoutAndReinstatement'
export default {
  components: {
    DynamicTabsContainer,
    HKLPinBadge,
  },
  mixins: [SwitchRegionsEventMixin],
  setup () {
    const canReadDirectory = useCanReadDirectoryDashboard()
    const { enableTenantProjectsFeature, enableGreenOperationFeature } = useStoreGetters('regions', ['enableTenantProjectsFeature', 'enableGreenOperationFeature'])
    const { currentUser, isHKlandUser } = useStoreGetters('user', ['currentUser', 'isHKlandUser'])
    const tabTemplates = ref([
      {
        tabName: i18n.t('handoverTakeback'),
        component: HandoverTakeback,
        i18nPathKey: 'handoverTakeback',
      },
      {
        tabName: i18n.t('tender.general.fitoutAndReinstatement'),
        component: MyTenantProjects,
        i18nPathKey: 'tender.general.fitoutAndReinstatement',
      },
      {
        tabName: i18n.t('dashboard.tab.greenOperation'),
        component: EngineerGreenOperationVue,
        i18nPathKey: 'dashboard.tab.greenOperation',
      },
      {
        tabName: i18n.t(I18N_KEY_DIRECTORY),
        component: Directories,
        i18nPathKey: I18N_KEY_DIRECTORY,
      },
    ])
    const canReadHotb = computed(() => {
      return unref(enableTenantProjectsFeature) && aclHelper.getAccess(tmsPermsTypes.hotb.read)
    })
    const canReadProject = computed(() => {
      return unref(enableTenantProjectsFeature) && aclHelper.getAccess(tmsPermsTypes.project.read)
    })
    const canReadGreenOperation = computed(() => {
      return unref(enableGreenOperationFeature) && aclHelper.getAccess(ccdPermsTypes.tenancy.read) && aclHelper.getAccess(ccdPermsTypes.tenancy.previewOperation)
    })
    const tabAccessList = computed(() => {
      return [unref(canReadHotb), unref(canReadProject), unref(canReadGreenOperation), unref(canReadDirectory)]
    })
    const shouldRenderTabTemplates = computed(() => {
      return unref(tabTemplates).filter((_, index) => {
      // filter with module permissions array that matches same index,
      // (using name ref will not trigger update such as this['canReadHotb'])
        return unref(tabAccessList)[index]
      })
    })
    const { tabPinIndex: directoryTabPinIndex, showTabPin: showDirectoryTabPin } = useDirectoryTabPin({
      shouldRenderTabTemplates,
      i18nPathKey: I18N_KEY_DIRECTORY,
    })
    const { tabPinIndex: hotbTabPinIndex, showTabPin: showHotbTabPin } = useHotbTabPin({
      shouldRenderTabTemplates,
      i18nPathKey: I18N_KEY_HANDOVER_TAKEBACK,
      canReadHotb,
      currentUser,
    })
    const { tabPinIndex: fitoutTabPinIndex, showTabPin: showFitoutTabPin } = useFitoutTabPin({
      shouldRenderTabTemplates,
      i18nPathKey: I18N_KEY_FITOUT_REINSTATEMENT,
      canReadProject,
      isHKlandUser,
      currentUser,
    })

    return {
      tabTemplates,
      tabAccessList,
      shouldRenderTabTemplates,
      directoryTabPinIndex,
      showDirectoryTabPin,
      hotbTabPinIndex,
      fitoutTabPinIndex,
      showHotbTabPin,
      showFitoutTabPin,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'currentUserContact']),
  },
  mounted () {
    if (!this.tabTemplates || this.tabTemplates.length === 0) {
      EventBus.emit('module_header_menu_open')
    }
  },
  methods: {
    replaceRouteWithTabIndex (activeKey) {
      this.$router.replace({
        name: this.$router.name,
        query: { tabIndex: activeKey },
      })
    },
  },
}
</script>

<style lang="scss">
@import '~styles/dashboard/tmsdashboard';
</style>
